import { AlertTitle, Checkbox, DialogContentText, FormControlLabel, Grid } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";

import { SessionContext } from "../../Context/SessionContext";
import { UserContext } from "../../Context/UserContext";
import { Customer, EnhancedCustomer } from "../../Models/Customer";
import { Error } from "../../Utils/Errors";
import OpsMappingUtility from "../../Utils/OpsMappingUtility";
import { useApi } from "../../Utils/useApi";
import CustomerTable from "../CustomerStepper/Steps/Search/SearchCustomerTable";
import AlertGridItem from "../Helpers/AlertGridItem";
import Modal from "../Helpers/Modal";

const DuplicateModal: React.FC = () => {
  const { centers } = useContext(UserContext);
  const {
    customer,
    duplicateCustomers,
    isSavingCustomer,
    lead,
    previewCustomer,
    showDuplicateModal,
    phoneNumberErrors,
    setPreviewCustomer,
    setDuplicateCustomers,
    setIsCustomerValidForOpsBooking,
    setSaveCustomer,
    setSelectedCustomer,
    setShowDuplicateModal
  } = useContext(SessionContext);
  const [override, setOverride] = useState(false);

  // on previewing a duplicate, do a full GET if the value is shallow
  const { response, isLoading } = useApi<Customer>({
    url: `Legacy/V0/Contacts/${lead.id}/Customers/${previewCustomer?.id}`,
    isCancelable: false,
    valid: !!previewCustomer?.shallow && showDuplicateModal,
    error: Error.GetCustomer
  });
  useEffect(() => {
    if (response) {
      // update the duplicate customers
      setDuplicateCustomers(oldCustomers => {
        const customers = [...oldCustomers];
        customers[customers.findIndex(c => c.id === response.id)] = response;
        return customers;
      });
      // if previewing, update preview customer
      setPreviewCustomer(oldPreview => {
        if (oldPreview?.id !== response.id || !oldPreview.shallow) return oldPreview;
        return new EnhancedCustomer({ ...response, center: centers.find(c => c.id === response.centerId) });
      });
    }
  }, [response, setDuplicateCustomers, setPreviewCustomer]);

  // when the customer is successfully POSTed, close the modal and clear the duplicates
  useEffect(() => {
    if (customer.id) {
      setShowDuplicateModal(false);
      setDuplicateCustomers([]);

      setIsCustomerValidForOpsBooking(OpsMappingUtility.isCustomerValidForBooking(customer));
    }
  }, [customer.id, setShowDuplicateModal, setDuplicateCustomers]);

  const handleConfirm = () => {
    if (override) setSaveCustomer({ override, customer });
    else setSelectedCustomer(customer);
  };
  const handleClose = () => {
    setPreviewCustomer(undefined);
    setShowDuplicateModal(false);
    setOverride(false);
  };
  const handleToggle = () =>
    setOverride(v => {
      if (!v) setPreviewCustomer(undefined);
      return !v;
    });

  // disable duplicate override if a zenoti record has identical first name and phone number
  const cannotOverride = duplicateCustomers.some(
    c =>
      c.firstName?.toLowerCase() === customer.firstName?.toLowerCase() &&
      !!c.phoneNumber &&
      c.phoneNumber === customer.phoneNumber
  );

  const phoneErrors = Object.entries(phoneNumberErrors).filter(([, value]) => value);

  return (
    <Modal
      open={showDuplicateModal}
      confirmDisabled={(!previewCustomer || previewCustomer.shallow) && !override}
      confirmSpinner={isLoading || isSavingCustomer}
      handleClose={handleClose}
      handleConfirm={handleConfirm}
      title="Duplicate Guest Found"
      maxWidth="md"
    >
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <DialogContentText>Guests have been found that conflict with your Guest in progress.</DialogContentText>
          <DialogContentText>
            Please select the correct Guest to prevent creating a duplicate and consider merging Guests in Zenoti.
          </DialogContentText>
        </Grid>
        <Grid item>
          <CustomerTable
            customers={duplicateCustomers}
            disabled={override}
            setPreviewCustomer={setPreviewCustomer}
            selectedCustomerId={previewCustomer?.id || undefined}
          />
        </Grid>
        <Grid item container direction="column" spacing={1}>
          <Grid item>
            <DialogContentText>
              Otherwise, if no guest above is an appropriate match, check the box below.
            </DialogContentText>
          </Grid>
          <Grid item>
            {!customer.id && <DialogContentText>Be absolutely sure as this will create a new guest.</DialogContentText>}
          </Grid>
          <Grid item sx={{ display: "flex", justifyContent: "center" }}>
            <FormControlLabel
              label={customer.id ? "No matches, continue with current guest." : "Create a New Guest"}
              control={
                <Checkbox
                  disabled={cannotOverride || Boolean(phoneErrors.length)}
                  color="primary"
                  onChange={handleToggle}
                  checked={override}
                />
              }
            />
          </Grid>
          <AlertGridItem severity="error" in={Boolean(phoneErrors.length)} spacing={1}>
            {`${phoneErrors.length} phone ${
              phoneErrors.length > 1 ? "numbers are" : "number is"
            } not allowed. See the ribbon for details. Cannot create new Guest.`}
          </AlertGridItem>
          <AlertGridItem severity="error" in={cannotOverride} spacing={1}>
            <AlertTitle>
              {customer.id ? "Switching customers is not optional." : "Cannot create a new Guest."}
            </AlertTitle>
            <div>An existing guest has an identical first name and mobile number.</div>
            <div>You must either change the guest information or switch to one of the above existing guests.</div>
          </AlertGridItem>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default DuplicateModal;
